import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { createTicket, getTickets, getTicket, addTicketMessage } from '../../services/api';
import { HelpCircle, Plus, MessageSquare, Clock, CheckCircle, AlertCircle, Send, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const Soporte = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    priority: 'medium'
  });

  useEffect(() => {
    if (user?.business_id) {
      loadTickets();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadTickets = async () => {
    try {
      const response = await getTickets({ business_id: user.business_id });
      setTickets(response.data);
    } catch (error) {
      console.error('Error loading tickets:', error);
      toast.error('Error al cargar tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    try {
      await createTicket({
        ...newTicket,
        business_id: user.business_id
      });
      toast.success('Ticket creado exitosamente');
      setShowNewTicketModal(false);
      setNewTicket({ subject: '', description: '', priority: 'medium' });
      loadTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Error al crear ticket');
    }
  };

  const handleSelectTicket = async (ticket) => {
    try {
      const response = await getTicket(ticket.id);
      setSelectedTicket(response.data);
    } catch (error) {
      console.error('Error loading ticket:', error);
      toast.error('Error al cargar ticket');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedTicket) return;
    
    setSendingMessage(true);
    try {
      await addTicketMessage(selectedTicket.id, newMessage);
      toast.success('Mensaje enviado');
      setNewMessage('');
      // Reload ticket to get new message
      const response = await getTicket(selectedTicket.id);
      setSelectedTicket(response.data);
      loadTickets();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error al enviar mensaje');
    } finally {
      setSendingMessage(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-yellow-100 text-yellow-700',
      in_progress: 'bg-blue-100 text-blue-700',
      resolved: 'bg-green-100 text-green-700',
      closed: 'bg-slate-100 text-slate-700'
    };
    const labels = {
      open: 'Abierto',
      in_progress: 'En Progreso',
      resolved: 'Resuelto',
      closed: 'Cerrado'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.open}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-slate-100 text-slate-600',
      medium: 'bg-orange-100 text-orange-700',
      high: 'bg-red-100 text-red-700'
    };
    const labels = {
      low: 'Baja',
      medium: 'Media',
      high: 'Alta'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[priority] || styles.medium}`}>
        {labels[priority] || priority}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!user?.business_id) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <HelpCircle className="w-16 h-16 text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">No hay negocio configurado</h2>
          <p className="text-slate-500">Primero debes crear tu negocio para acceder a soporte.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Soporte</h1>
            <p className="text-slate-600 mt-2">¿Necesitas ayuda? Estamos aquí para ti</p>
          </div>
          <button
            onClick={() => setShowNewTicketModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
            data-testid="new-ticket-btn"
          >
            <Plus className="w-4 h-4" />
            Nuevo Ticket
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tickets List */}
          <div className="lg:col-span-1 bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <h2 className="font-semibold text-slate-900">Mis Tickets</h2>
            </div>
            <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
              {tickets.length === 0 ? (
                <div className="p-6 text-center text-slate-500">
                  <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p>No tienes tickets aún</p>
                  <p className="text-sm">Crea uno si necesitas ayuda</p>
                </div>
              ) : (
                tickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    onClick={() => handleSelectTicket(ticket)}
                    className={`w-full p-4 text-left hover:bg-slate-50 transition-colors ${
                      selectedTicket?.id === ticket.id ? 'bg-teal-50 border-l-4 border-teal-600' : ''
                    }`}
                    data-testid={`ticket-${ticket.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{ticket.subject}</p>
                        <p className="text-sm text-slate-500 truncate mt-1">{ticket.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          {getStatusBadge(ticket.status)}
                          {getPriorityBadge(ticket.priority)}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      {new Date(ticket.created_at).toLocaleDateString('es-MX')}
                    </p>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Ticket Detail / Chat */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden">
            {selectedTicket ? (
              <div className="flex flex-col h-[600px]">
                {/* Header */}
                <div className="p-4 border-b border-slate-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="font-semibold text-slate-900">{selectedTicket.subject}</h2>
                      <div className="flex items-center gap-2 mt-2">
                        {getStatusBadge(selectedTicket.status)}
                        {getPriorityBadge(selectedTicket.priority)}
                      </div>
                    </div>
                    <p className="text-sm text-slate-500">
                      {new Date(selectedTicket.created_at).toLocaleDateString('es-MX', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
                  {selectedTicket.messages?.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.sender_type === 'admin' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 ${
                          message.sender_type === 'admin'
                            ? 'bg-white border border-slate-200'
                            : 'bg-teal-600 text-white'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        <p className={`text-xs mt-1 ${
                          message.sender_type === 'admin' ? 'text-slate-400' : 'text-teal-100'
                        }`}>
                          {message.sender_type === 'admin' ? 'Soporte' : 'Tú'} • 
                          {new Date(message.created_at).toLocaleTimeString('es-MX', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Input */}
                {selectedTicket.status !== 'closed' && (
                  <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-200">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Escribe tu mensaje..."
                        className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:outline-none"
                        disabled={sendingMessage}
                      />
                      <button
                        type="submit"
                        disabled={!newMessage.trim() || sendingMessage}
                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Send className="w-5 h-5" />
                      </button>
                    </div>
                  </form>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[600px] text-slate-500">
                <MessageSquare className="w-16 h-16 text-slate-300 mb-4" />
                <p className="font-medium">Selecciona un ticket</p>
                <p className="text-sm">para ver la conversación</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Ticket Modal */}
      {showNewTicketModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-2xl font-bold text-slate-900">Nuevo Ticket de Soporte</h2>
              <p className="text-sm text-slate-600 mt-1">Describe tu problema y te ayudaremos</p>
            </div>
            <form onSubmit={handleCreateTicket} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Asunto</label>
                <input
                  type="text"
                  required
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  placeholder="Ej: Problema con el inventario"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Prioridad</label>
                <select
                  value={newTicket.priority}
                  onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                >
                  <option value="low">Baja - Consulta general</option>
                  <option value="medium">Media - Problema que afecta operación</option>
                  <option value="high">Alta - Urgente, no puedo operar</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Descripción</label>
                <textarea
                  required
                  rows={4}
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  placeholder="Describe tu problema con el mayor detalle posible..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 resize-none"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
                >
                  Crear Ticket
                </button>
                <button
                  type="button"
                  onClick={() => setShowNewTicketModal(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Soporte;
